import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import Moment from 'moment';
import axios from 'axios';
import { Collapse } from 'react-bootstrap';
import { AppDatePicker, AppCard, AppNoInfo } from '@/layout/AppComponents';
import { toFormData } from '../../../minions/forms';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../routing/helpers/router';
import ModuleQseSideBarReports from './ModuleQseSideBarReports';
import QseReportDataSectionRow from '../../../layout/screenSpecifics/Modules/QseReports/QseReportDataSectionRow';
import QseReportDataSectionTitle from '../../../layout/screenSpecifics/Modules/QseReports/QseReportDataSectionTitle';
import NotificationAlert from '../../../layout/components/helpers/notificationAlert';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import PreventNavigation from '../../../layout/components/helpers/PreventNavigation';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import _ from 'lodash';

const ModuleQseReportScreen = (props) => {
  const { reportType } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const formikRef = useRef();
  const global = useSelector((state) => state.global);
  const hasOmActivities = useSelector(
    (s) => s.currentPage?.qseReports?.hasOmActivities,
  );
  const omActivitiesInitialDate = useSelector(
    (s) => s.currentPage?.qseReports?.omActivitiesInitialDate,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [filterDate, setFilterDate] = useState(Moment);
  const [collapseOpened, setCollapseOpened] = useState([]);
  const [report, setReport] = useState();
  const [initialValues, setInitialValues] = useState();
  const [moduleData, setModuleData] = useState();

  const loadReport = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_qseReports_getReportData'), {
          reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          date: filterDate.format('DD/MM/YYYY'),
        })
        .then((r) => {
          if (isMounted.current) {
            setReport(r.data);
            setIsLoading(false);
            setInitialValues();
          }
        }),
    [filterDate],
  );

  useEffect(() => {
    setIsLoading(true);
    loadReport();
  }, [filterDate]);

  useEffect(() => {
    const ia = {};
    moduleData?.indicators?.[reportType]?.optionHeaders.map((oh) => {
      oh.options.map((o) => {
        ia[`${o.code}-value`] = report?.settings?.[o.code]?.value || '';
        ia[`${o.code}-comments`] = report?.settings?.[o.code]?.comments || '';
        ia[`${o.code}-removeFiles`] =
          report?.settings?.[o.code]?.removeFiles || [];
      });
    });
    setInitialValues(ia);
  }, [report]);

  const handleOnSubmit = useCallback(
    async (values) => {
      await axios
        .post(
          axiosPath('api_r_user_module_qseReports_editReport'),
          toFormData({
            ...values,
            reportType,
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            date: filterDate.format('DD/MM/YYYY'),
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then((r) => {
          if (isMounted.current) {
            setReport(r.data);
            NotificationAlert({
              title: 'Completed',
              message: 'The report has been saved successfully',
              type: 'success',
              duration: 5000,
              position: 'top-right',
            });

            moduleData?.indicators?.[reportType]?.optionHeaders.map((oh) =>
              oh.options.map((o) =>
                formikRef.current.setFieldValue(`${o.code}-files`, []),
              ),
            );
          }
        })
        .catch((err) => {
          dispatch(errorHandler(err));
        });
    },
    [
      reportType,
      moduleBridgeHex,
      subModuleBridgeHex,
      plantHex,
      filterDate,
      moduleData,
      formikRef,
    ],
  );
  // < SAVE FORM

  // < TOGGLE HASOMACTIVITIES
  const handleHasOmActivities = async () => {
    setIsLoading(true);
    await axios
      .post(axiosPath('api_r_user_module_qseReports_toggleQualityOmBlock'), {
        reportType,
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change('qseReports', r.data));
          setIsLoading(false);
          NotificationAlert({
            title: 'Completed',
            message: 'The configuration has been saved successfully',
            type: 'success',
            duration: 5000,
            position: 'top-right',
          });
        }
      })
      .catch(() => {
        setIsLoading(false);
        NotificationAlert({
          title: 'Error',
          message: 'Error',
          type: 'danger',
          duration: 5000,
          position: 'top-right',
        });
      });
  };
  // > TOGGLE HASOMACTIVITIES

  // < EDIT OMACTIVITIES INITIAL DATE
  const handleOmActivitiesInitialDate = async (date) => {
    setIsLoading(true);
    await axios
      .post(
        axiosPath('api_r_user_module_qseReports_editQualityOmBlockInitialDate'),
        {
          reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          omActivitiesInitialDate: date,
        },
      )
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change('qseReports', r.data));
          setIsLoading(false);
          NotificationAlert({
            title: 'Completed',
            message: 'The configuration has been saved successfully',
            type: 'success',
            duration: 5000,
            position: 'top-right',
          });
        }
      })
      .catch(() => {
        setIsLoading(false);
        NotificationAlert({
          title: 'Error',
          message: 'Error',
          type: 'danger',
          duration: 5000,
          position: 'top-right',
        });
      });
  };
  // > TOGGLE HASOMACTIVITIES

  const toggleCollapse = (index) => {
    if (collapseOpened.includes(index)) {
      setCollapseOpened(collapseOpened.filter((i) => i !== index));
    } else {
      const newCollapseOpened = [...collapseOpened];
      newCollapseOpened.push(index);
      setCollapseOpened(newCollapseOpened);
    }
  };

  const handleReport = () =>
    axios
      .post(axiosPath('api_r_user_module_qseReports_getXlsReport'), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
        filterDate: filterDate.format('DD-MM-YYYY'),
      })
      .then(({ data }) => (window.location = data))
      .catch((e) => errorHandler(e));

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_qseReports_getModuleData'),
        { moduleBridgeHex, subModuleBridgeHex, plantHex },
      );
      return response.data;
    } catch (err) {}
  }, []);

  useEffect(() => {
    loadModuleData().then((r) => {
      if (isMounted.current) {
        setModuleData(r);
        dispatch(
          currentPageAction.change('qseReports', {
            hasOmActivities: r.hasOmActivities,
            omActivitiesInitialDate: r.omActivitiesInitialDate,
          }),
        );
      }
    });
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - Assessment`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: 'QHSE Reports',
                  route: {
                    name: 'user_module_qseReports_home',
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: moduleData?.indicators?.[reportType]?.name,
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleQseSideBarReports
          activeRoute={`user_module_qseReports_${reportType}`}
          moduleData={moduleData}
        />
        <AppCard header={moduleData?.indicators?.[reportType]?.name}>
          {initialValues && !isLoading ? (
            <Formik
              innerRef={formikRef}
              initialValues={initialValues}
              validateOnChange={false}
              validateOnBlur={false}
              enableReinitialize
              onSubmit={handleOnSubmit}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                isSubmitting,
                dirty,
                resetForm,
              }) => (
                <form onSubmit={handleSubmit}>
                  {dirty && (
                    <PreventNavigation message="This page is asking you to confirm that you want to leave — information you've entered may not be saved." />
                  )}
                  {reportType === 'quality' && (
                    <div className="row">
                      <div className="col-12 col-sm-6 col-md-8 col-lg-9">
                        <label className="mb-2 pointer">
                          <input
                            type="checkbox"
                            className="mr-1"
                            value={hasOmActivities}
                            onChange={() =>
                              dirty ? undefined : handleHasOmActivities()
                            }
                            checked={hasOmActivities}
                          />
                          Do you carry out O&M activities?
                        </label>
                      </div>
                      {hasOmActivities && (
                        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3 float-right">
                          <AppDatePicker
                            title="Initial date O&M FRV"
                            value={omActivitiesInitialDate}
                            onChange={(date) =>
                              handleOmActivitiesInitialDate(date)
                            }
                            dropdownMode="select"
                            showMonthDropdown
                            showYearDropdown
                            isClearable
                            readOnly={dirty}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <div className="row">
                    <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                      <AppDatePicker
                        title="Month"
                        value={filterDate.format('MM/YYYY')}
                        onChange={(date) =>
                          setFilterDate(
                            Moment(date, 'MM/YYYY').startOf('month'),
                          )
                        }
                        dateFormat="MM/Y"
                        displayDateFormat="MM/YYYY"
                        dropdownMode="select"
                        showMonthDropdown
                        showYearDropdown
                        showMonthYearPicker
                        disabled={isSubmitting}
                        minDate={new Date(2020, 0, 1)}
                        maxDate={Moment().toDate()}
                        readOnly={dirty}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 mb-2">
                      <span
                        className="pointer"
                        onClick={() => setCollapseOpened([])}
                      >
                        Collapse all
                      </span>{' '}
                      /{' '}
                      <span
                        className="pointer"
                        onClick={() =>
                          setCollapseOpened(
                            _.map(
                              moduleData?.indicators[reportType]?.optionHeaders,
                              'code',
                            ),
                          )
                        }
                      >
                        Uncollapse all
                      </span>
                      <button
                        type="submit"
                        className="btn btn-primary btn-xs float-right"
                        disabled={isSubmitting}
                      >
                        Save
                      </button>
                      {dirty && (
                        <button
                          type="button"
                          className="btn btn-danger btn-xs float-right mr-2"
                          onClick={resetForm}
                        >
                          Undo changes
                        </button>
                      )}
                      <span
                        onClick={handleReport}
                        className="btn btn-success btn-xs float-right mr-2"
                      >
                        Download report
                      </span>
                    </div>
                  </div>
                  <div className="clearfix" />
                  <div className="row">
                    <div className="col-12 mb-2">
                      {moduleData?.indicators?.[reportType]?.optionHeaders.map(
                        (oh) => (
                          <React.Fragment key={oh.code}>
                            {(oh.code !== 'omKpi' || hasOmActivities) && (
                              <>
                                <QseReportDataSectionTitle
                                  title={oh.name}
                                  handleClick={() => toggleCollapse(oh.code)}
                                />
                                <Collapse in={collapseOpened.includes(oh.code)}>
                                  <div>
                                    {oh.options.map((o) => (
                                      <React.Fragment key={o.code}>
                                        <QseReportDataSectionRow
                                          reportType={reportType}
                                          moduleBridgeHex={moduleBridgeHex}
                                          subModuleBridgeHex={
                                            subModuleBridgeHex
                                          }
                                          plantHex={plantHex}
                                          reportID={report?.id}
                                          code={o.code}
                                          title={o.name}
                                          value={values[`${o.code}-value`]}
                                          comments={
                                            values[`${o.code}-comments`]
                                          }
                                          currentFiles={
                                            report?.settings?.[o.code]?.files ||
                                            []
                                          }
                                          formFiles={
                                            values[`${o.code}-files`] || []
                                          }
                                          removeFiles={
                                            values[`${o.code}-removeFiles`]
                                          }
                                          clarifications={o.clarifications}
                                          handleOnSetFieldValue={setFieldValue}
                                        />
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </Collapse>
                              </>
                            )}
                          </React.Fragment>
                        ),
                      )}
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          ) : (
            <AppNoInfo text="Loading..." />
          )}
        </AppCard>
      </section>
    </div>
  );
};

export default ModuleQseReportScreen;
